import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'

import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'

import {
    buttonSx,
    chipSx,
    classToFComponent,
    FCClassContext,
    LifesHubCard,
    LifesHubEmptyState,
    ModuleView,
    PageSubtitle,
    PageTitle
} from 'src/utils/views'
import { EnumModuleNames, ModuleCard, OverviewScope } from '../overview_scopes'

const LOG = Logger.get('Overview-VIEW')

const PageView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '26px 28px 42px',
    flex: '1 1 0',
    minWidth: 0,
    '@media (max-width: 900px)': {
        padding: '22px 18px 36px'
    }
})

const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 12,
    flexShrink: 0,
    '@media (max-width: 720px)': {
        flexDirection: 'column',
        gap: 12
    }
})

const ModuleGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 400px))',
    gap: 8,
    alignItems: 'stretch',
    '@media (max-width: 1050px)': {
        gridTemplateColumns: 'repeat(2, minmax(0, 400px))'
    },
    '@media (max-width: 720px)': {
        gridTemplateColumns: 'minmax(0, 1fr)'
    }
})

const ProductCard = styled(LifesHubCard)({
    padding: 20
})

const IconBox = styled('div')({
    width: 42,
    height: 42,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 11,
    color: 'var(--green-800)',
    backgroundColor: 'var(--green-100)',
    '& .MuiSvgIcon-root': { fontSize: 24 }
})

const ProductTitle = styled('h3')({
    margin: '14px 0 0',
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--ink)',
    lineHeight: 1.2
})

const BulletList = styled('ul')({
    listStyle: 'none',
    margin: '14px 0 4px',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
})

const BulletItem = styled('li')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 13,
    lineHeight: 1.45,
    color: 'var(--muted)',
    '& .MuiSvgIcon-root': {
        fontSize: 16,
        marginTop: 2,
        flexShrink: 0,
        color: 'var(--green-800)'
    }
})

const ProductDescription = styled('p')({
    margin: '8px 0 0',
    color: 'var(--muted)'
})

const PageFooter = styled('div')({
    marginTop: 22,
    textAlign: 'center',
    color: 'var(--muted)',
    fontSize: 12
})

const useStyles = makeStyles()({
    cardTop: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12
    },
    cardSpacer: {
        flexGrow: 1,
        minHeight: 14
    }
})

type OverviewViewProps = {
    className?: string
    scope: OverviewScope
}

class OverviewClass implements FCClassContext<OverviewViewProps> {
    // :: emissors
    readonly emitOpenModule = (module: EnumModuleNames) => {
        this.scope.onOpenModule(module).catch(LOG.caught)
    }

    readonly getIcon = (moduleCard: ModuleCard) => {
        const Icon = moduleCard.icon
        return <Icon />
    }

    // :: Fields
    scope!: OverviewScope
    classes!: ReturnType<typeof useStyles>['classes']

    // :: Methods

    onSyncState({ scope }: OverviewViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className, scope }: OverviewViewProps) {
        this.scope = bindUpdate(React, scope)

        const modules = this.scope.modules.filter((m) => m.active)

        return (
            <ModuleView className={className}>
                <PageView>
                    <PageHead>
                        <div>
                            <PageTitle>Início</PageTitle>
                            <PageSubtitle>
                                Selecione um produto para explorar os dados detalhados. Cada um abre com os filtros e a
                                busca próprios do módulo.
                            </PageSubtitle>
                        </div>
                    </PageHead>

                    {modules.length === 0 ? this.renderEmpty() : this.renderModules(modules)}

                    <PageFooter>© LifesHub Intelligence. Dados protegidos e monitorados.</PageFooter>
                </PageView>
            </ModuleView>
        )
    }

    renderModules(modules: ModuleCard[]) {
        const classes = this.classes

        return (
            <ModuleGrid>
                {modules.map((m) => (
                    <ProductCard key={m.name}>
                        <div className={classes.cardTop}>
                            <IconBox>{this.getIcon(m)}</IconBox>
                            <Chip size="small" label="Módulo" sx={chipSx('gray')} />
                        </div>

                        <ProductTitle>{m.name}</ProductTitle>
                        <ProductDescription>{m.description}</ProductDescription>

                        {!!m.bullets?.length && (
                            <BulletList>
                                {m.bullets.map((bullet) => (
                                    <BulletItem key={bullet}>
                                        <CheckRoundedIcon />
                                        <span>{bullet}</span>
                                    </BulletItem>
                                ))}
                            </BulletList>
                        )}

                        <div className={classes.cardSpacer} />

                        <Button
                            sx={{ ...buttonSx(), '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 } }}
                            fullWidth
                            onClick={() => this.emitOpenModule(m.moduleName)}
                            endIcon={<ChevronRightIcon />}
                        >
                            Acessar o produto
                        </Button>
                    </ProductCard>
                ))}
            </ModuleGrid>
        )
    }

    renderEmpty() {
        return (
            <LifesHubCard>
                <LifesHubEmptyState
                    title="Nenhum produto disponível"
                    text="Seu acesso ainda não contempla nenhum dos produtos. Peça a liberação ao administrador da sua organização."
                />
            </LifesHubCard>
        )
    }
}

export const OverviewView = classToFComponent(OverviewClass, React)
export default OverviewView
